import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import StoriesPage from "./pages/StoriesPage";
import StoryReaderPage from "./pages/StoryReaderPage";
import StoryEditorPage from "./pages/StoryEditorPage";
import MyStoriesPage from "./pages/MyStoriesPage";
import ChallengesPage from "./pages/ChallengesPage";
import ChallengeDetailPage from "./pages/ChallengeDetailPage";
import ChallengeResultsPage from "./pages/ChallengeResultsPage";
import JudgePanelPage from "./pages/JudgePanelPage";
import ReadingPage from "./pages/ReadingPage";
import GuestAuthorsPage from "./pages/GuestAuthorsPage";
import GroupsPage from "./pages/GroupsPage";
import GroupDetailPage from "./pages/GroupDetailPage";
import MessagesPage from "./pages/MessagesPage";
import SpotlightPage from "./pages/SpotlightPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import DiscoverWritersPage from "./pages/DiscoverWritersPage";
import WriterProfilePage from "./pages/WriterProfilePage";
import SectionsPage from "./pages/SectionsPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import ReadingListPage from "./pages/ReadingListPage";
import AchievementsPage from "./pages/AchievementsPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FAQPage from "./pages/FAQPage";
import BookDiscussionDetailPage from "./pages/BookDiscussionDetailPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import MembersPage from "./pages/MembersPage";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Moderator Route component
const ModeratorRoute = ({ children }) => {
  const { user, loading, isModerator } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }
  
  if (!user || !isModerator) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout component with Navbar and Footer
const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
      {/* Subtle noise overlay for texture */}
      <div className="noise-overlay" />
    </div>
  );
};

// Layout without footer (for editor)
const EditorLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      {children}
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout><LandingPage /></Layout>} />
      <Route path="/login" element={<Layout><LoginPage /></Layout>} />
      <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
      <Route path="/verify-email" element={<Layout><VerifyEmailPage /></Layout>} />
      <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
      <Route path="/reset-password" element={<Layout><ResetPasswordPage /></Layout>} />
      <Route path="/stories" element={<Layout><StoriesPage /></Layout>} />
      <Route path="/story/:id" element={<Layout><StoryReaderPage /></Layout>} />
      <Route path="/challenges" element={<Layout><ChallengesPage /></Layout>} />
      <Route path="/challenges/:id" element={<Layout><ChallengeDetailPage /></Layout>} />
      <Route path="/challenges/:id/results" element={<Layout><ChallengeResultsPage /></Layout>} />
      <Route path="/challenges/:id/judge-panel" element={<Layout><ProtectedRoute><JudgePanelPage /></ProtectedRoute></Layout>} />
      <Route path="/reading" element={<Layout><ReadingPage /></Layout>} />
      <Route path="/reading/:id" element={<Layout><BookDiscussionDetailPage /></Layout>} />
      <Route path="/guest-authors" element={<Layout><GuestAuthorsPage /></Layout>} />
      <Route path="/guest-authors/:id" element={<Layout><GuestAuthorsPage /></Layout>} />
      <Route path="/groups" element={<Layout><GroupsPage /></Layout>} />
      <Route path="/groups/:id" element={<Layout><GroupDetailPage /></Layout>} />
      <Route path="/spotlight" element={<Layout><SpotlightPage /></Layout>} />
      <Route path="/discover" element={<Layout><DiscoverWritersPage /></Layout>} />
      <Route path="/members" element={<Layout><MembersPage /></Layout>} />
      <Route path="/sections" element={<Layout><SectionsPage /></Layout>} />
      <Route path="/guidelines" element={<Layout><CommunityGuidelinesPage /></Layout>} />
      <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
      <Route path="/community-board" element={<Layout><AnnouncementsPage /></Layout>} />
      <Route path="/reading-list" element={<Layout><ReadingListPage /></Layout>} />
      <Route path="/achievements" element={<Layout><AchievementsPage /></Layout>} />
      <Route path="/writer/:id" element={<Layout><WriterProfilePage /></Layout>} />
      <Route path="/about" element={<Layout><LandingPage /></Layout>} />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/write" element={
        <ProtectedRoute>
          <EditorLayout><StoryEditorPage /></EditorLayout>
        </ProtectedRoute>
      } />
      <Route path="/story/:id/edit" element={
        <ProtectedRoute>
          <EditorLayout><StoryEditorPage /></EditorLayout>
        </ProtectedRoute>
      } />
      <Route path="/my-stories" element={
        <ProtectedRoute>
          <Layout><MyStoriesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Layout><MessagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ModeratorRoute>
          <Layout><AdminPage /></Layout>
        </ModeratorRoute>
      } />
      <Route path="/admin/*" element={
        <ModeratorRoute>
          <Layout><AdminPage /></Layout>
        </ModeratorRoute>
      } />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
